import React, { useContext, useEffect, useState } from "react";
import { Box, Menu, MenuItem, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import globalStyles from "../../../../assets/styles/global.module.scss";
import styles from "../../../../assets/styles/componentStyles/templateLibraryStyles/whatsappTemplatePreview.module.scss";

import FileUpload from "../../../utility/fileUpload/fileUpload";
import IconsColor from "../../../utility/iconsColor";
import WrapWithHoverUI from "../utils/WrapWithHoverUI";
import VariableDialog from "../utils/VariableDialog";
import { getWhatsappMediaAllowedFileTypes, getWhatsappMediaSizeLimit } from "../../../../utils/constants";
import { renderVariableInText } from "../utils/renderVariableInText";
import { cleanFilename, disableHeaderType, headerTypeIconMap, LOCATION_HEADER_FIELDS, getLocationHeaderSampleWarning } from "../constant";
import { useTemplateLibraryStore } from "../../../../store/templateLibraryStore";
import defaultWATemplateImg from "../../../../assets/images/messageFormats/defaultWATemplateImage.png";
import { createWhatsappTemplatePayloadForTemplateLibrary } from "../../../../models/axiosModel";
import { canUpdateTemplate, getTextCharacterLimit, getVariableRegex, isCallPermissionRequestTemplate, isCatalogTemplate, isTemplateCategoryAuthentication, maxHeaderTextChars } from "../../channels/global/whatsapp/templates/utils";
import { useSearchParams } from "react-router-dom";
import { greyColor, whiteColor } from "../../../../utils/colors";
import { isTemplateCreated } from "../constant";
import { useCarouselFunctions } from "../utils/useCarouselFunctions";
import { getVendorField } from "../../../../utils/whiteLabelling";
import { UserContext, UserContextPayload } from "../../../../context/userContext";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const WhatsAppMessageHeaderPreview = (props: any) => {

    const { template, editable, compact, uploadFileHandler, removeFileHandler, handleChangeText, 
        handleAddBodyVariableManually, removeVariableData, handleChange, handleRemoveField, headerTextareaRef, onChangeOfHeader, 
        isFileUploaded } = props;

    const whatsappTemplateHeaderTypes = useTemplateLibraryStore((state) => state.whatsappTemplateHeaderTypes);
    const files = useTemplateLibraryStore((state) => state.files);
    const createTemplatePayload = useTemplateLibraryStore((state) => state.createTemplatePayload);
    const templateStatusDetails = useTemplateLibraryStore((state) => state.templateStatusDetails);
    const setCreateTemplatePayload = useTemplateLibraryStore((state) => state.setCreateTemplatePayload);
    const interactiveTypeList = useTemplateLibraryStore((state) => state.interactiveTypeList);
    const templateLibraryCategoryList = useTemplateLibraryStore((state) => state.templateLibraryCategoryList);
    const whatsappTemplateTypes = useTemplateLibraryStore((state) => state.whatsappTemplateTypes);

    const { vendorData } = useContext(UserContext) as UserContextPayload;

    const { isMobileScreen } = useCarouselFunctions();

    const [hover, setHover] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [editingText, setEditingText] = useState(false);
    const [editingMedia, setEditingMedia] = useState(false);
    const [variableDialogOpen, setVariableDialogOpen] = useState(false);
    const [variableDialogAnchorEl, setVariableDialogAnchorEl] = useState<HTMLElement | null>(null);
    const [activeVariableKey, setActiveVariableKey] = useState<string | null>(null);
    
    const [isEditable, setIsEditable] = useState(editable);
    const [searchParams] = useSearchParams();
    let draftId = searchParams.get('draft_id');

    // Constants
    const isAuthenticationTemplate = isTemplateCategoryAuthentication(templateLibraryCategoryList, createTemplatePayload?.whatsapp_template_data?.whatsapp_template_category_id);
    const isTemplateTypeCatalog = isCatalogTemplate(whatsappTemplateTypes, createTemplatePayload?.whatsapp_template_type_id);
    const isTemplateTypeCallPermissionRequest = isCallPermissionRequestTemplate(whatsappTemplateTypes, createTemplatePayload?.whatsapp_template_type_id);

    const headerType = template?.headerType ?? "none"; 

    const headerTypeOptions = whatsappTemplateHeaderTypes
        ?.filter((headerType: any) => !isTemplateCreated(createTemplatePayload) && !disableHeaderType(headerType?.whatsapp_template_header_type_name, createTemplatePayload, interactiveTypeList, isAuthenticationTemplate, isTemplateTypeCallPermissionRequest))
        ?.map((headerType: any) => ({
            name: headerType?.whatsapp_template_header_type_name,
            id: headerType?.whatsapp_template_header_type_id,
        })) ?? [];

    const options = [{ name: "None" }, ...headerTypeOptions];

    const selectedHeaderTypeName = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_type_name
        ?? (template?.headerType ? (template.headerType.charAt(0).toUpperCase() + template.headerType.slice(1)) : "None");

    const isHeaderOptionSelected = (optionName: string) => selectedHeaderTypeName?.toLowerCase() === optionName?.toLowerCase();

    const hasHeader = template?.headerType && template.headerType !== "none";

    // Functions
    const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleCloseMenu = () => setAnchorEl(null);

    const handleSelectType = (typeName: string) => {
        onChangeOfHeader(typeName);
        handleCloseMenu();
    };

    const handleVariableAreaClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const variableElement = target.closest?.('[data-variable="true"]') || target.closest?.(`.${styles.whatsappPreview__variable}`);

        if (variableElement) {
            e.preventDefault();
            e.stopPropagation();
            setVariableDialogAnchorEl(variableElement as HTMLElement);
            setActiveVariableKey((variableElement as HTMLElement).getAttribute("data-variable-key"));
            setVariableDialogOpen(true);
        } else if (isEditable) {
            setEditingText(true);
        }
    };

    const handleVariableDialogClose = () => {
        setVariableDialogOpen(false);
        setVariableDialogAnchorEl(null);
        setActiveVariableKey(null);
    };

    const handleHeaderDisabled = () => {
        return (
            Boolean(createTemplatePayload?.whatsapp_template_id) || isAuthenticationTemplate
        )
    }

    const wrapWithHoverUI = (content: any) => (
        <WrapWithHoverUI
            label="Header"
            editable={editable}
            showEditIcon={hasHeader && isEditable && !isTemplateCreated(createTemplatePayload)}
            onEditClick={isEditable && !isTemplateCreated(createTemplatePayload) ? handleOpenMenu : undefined}
            onHoverChange={setHover}
            onMouseLeave={() => setEditingText(false)}
            editContent={
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    {options?.map((opt) => (
                        <MenuItem
                            key={opt?.name}
                            onClick={() => handleSelectType(opt?.name)}
                            disabled={handleHeaderDisabled()}
                            className={isHeaderOptionSelected(opt?.name) ? styles.whatsappPreview__headerTypeMenuItemSelected : undefined}
                        >
                            <Box className={`${styles.whatsappPreview__headerTypeMenuItem} ${globalStyles.clickable}`} >
                                {headerTypeIconMap[opt?.name?.toLowerCase() as keyof typeof headerTypeIconMap] ?? null}
                                <span>
                                    {opt?.name}
                                </span>
                            </Box>
                        </MenuItem>
                    ))}
                </Menu>
            }
        >
            {content}
        </WrapWithHoverUI>
    );

    // Use Effects
    useEffect(() => {
        if (!editable) {
            setIsEditable(false);
            return;
        }
        setIsEditable(
            !compact && canUpdateTemplate(templateStatusDetails?.whatsapp_template_metadata?.meta_whatsapp_template_status_name, createTemplatePayload, whatsappTemplateTypes, draftId)
        );
    }, [editable, compact, templateStatusDetails, createTemplatePayload, whatsappTemplateTypes, draftId]);

    useEffect(() => {
        if (!isEditable) {
            setAnchorEl(null);
            // setVariableDialogOpen(false);
            // setVariableDialogAnchorEl(null);
            setEditingText(false);
            setEditingMedia(false);
        }
    }, [isEditable]);

    useEffect(() => {
        if (!editable) {
            setVariableDialogOpen(false);
            setVariableDialogAnchorEl(null);
            setActiveVariableKey(null);
        }
    }, [editable]);

    useEffect(() => {
        if (editingText && headerTextareaRef?.current) {
            requestAnimationFrame(() => {
                const headerTextareaElement = headerTextareaRef?.current;
                if (!headerTextareaElement) return;
                headerTextareaElement.focus();
                const headerValueLength = (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_data_value ?? '').length;
                headerTextareaElement.setSelectionRange(headerValueLength, headerValueLength);
            });
        }
    }, [editingText, headerTextareaRef]);
    
    if(isTemplateTypeCatalog) return null;

    switch (headerType) {
        case undefined:
        case "none": {
            if (isEditable && !createTemplatePayload?.whatsapp_template_id && !isAuthenticationTemplate) {
                return wrapWithHoverUI(
                    <Box
                        onClick={handleOpenMenu}
                        className={`${styles.whatsappPreview__addHeader} ${globalStyles.clickable}`}
                    >
                        <AddIcon fontSize="small" />
                        <Typography variant={isMobileScreen ? "subtitle1" : "body2"}>Add Header</Typography>
                    </Box>
                );
            }
            return null;
        }

        case "text": {
            if (!template.header) return null;
            const content = template?.header?.content ?? "";

            const variableData = editable
                ? (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data ?? [])
                : (template?.header?.variableData || []);
            
            const hasMissingSampleText = variableData.some(
                (variable: any) => !variable?.whatsapp_template_dynamic_variable_sample_value || variable?.whatsapp_template_dynamic_variable_sample_value?.trim() === ""
            );
            const isEmptyText = !content.trim();

            const renderTextHeaderPreview = (placeholderText: string = "") => (
                <Box
                    className={`${styles.whatsappPreview__headerText} ${editable && (hasMissingSampleText || isEmptyText) ? styles.whatsappPreview__textInputError : ""}`}
                    dir="auto"
                >
                    {content?.trim()
                        ? renderVariableInText(content, variableData)
                        : (
                            <span
                                className={styles.whatsappPreview__textContentPreview}
                                dangerouslySetInnerHTML={{ __html: `<strong>${placeholderText}</strong>` }}
                            />
                        )}
                </Box>
            );

            return (
                <>
                    {editable ? (
                        wrapWithHoverUI(
                            <div onClick={handleVariableAreaClick} className={styles.whatsappPreview__editableTextFieldWrapper}>
                                {editingText ? (
                                    <TextField
                                        fullWidth
                                        multiline
                                        // disabled={createTemplatePayload?.whatsapp_template_id ? true : false}
                                        disabled={!canUpdateTemplate(templateStatusDetails?.whatsapp_template_metadata?.meta_whatsapp_template_status_name, createTemplatePayload, whatsappTemplateTypes, draftId)}
                                        inputRef={headerTextareaRef}
                                        className={styles.whatsappPreview__headerText}
                                        inputProps={{ dir: 'auto' }}
                                        InputProps={{ className: `${styles.whatsappPreview__textInputHoverContainer}` }}
                                        value={createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_data_value ?? ''}
                                        onChange={(event: any) => {
                                            const { value } = event.target;
                                            if (value.endsWith('{{')) {
                                                handleAddBodyVariableManually(value, "header")
                                            } else {
                                                const transformedValue = value.replace(getVariableRegex(createTemplatePayload?.flag_named_parameter), '*');
                                                let maximumHeaderCharacterLimit = getTextCharacterLimit(createTemplatePayload, interactiveTypeList, "header text limit") ?? maxHeaderTextChars;

                                                if (transformedValue.length <= maximumHeaderCharacterLimit) {
                                                    let finalValues = removeVariableData(value, "header")
                                                    handleChangeText(event, 'header');
                                                    const updatedPayload: createWhatsappTemplatePayloadForTemplateLibrary = {
                                                        ...createTemplatePayload,
                                                        whatsapp_template_data: {
                                                            ...createTemplatePayload?.whatsapp_template_data,
                                                            whatsapp_template_header: {
                                                                ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header,
                                                                whatsapp_template_header_data: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.map((variable: any) => {
                                                                    return {
                                                                        ...variable,
                                                                        whatsapp_template_header_data_value: finalValues?.textAreaValue,
                                                                        whatsapp_template_header_variable_data: finalValues?.variableData,
                                                                    }
                                                                })
                                                            }
                                                        }
                                                    };
                                                    setCreateTemplatePayload(updatedPayload);
                                                }
                                            }
                                        }}
                                        autoComplete='off'
                                        minRows={1}
                                        placeholder='Enter header text'
                                    />
                                ) : (
                                    renderTextHeaderPreview("Enter your header text here")
                                )}
                            </div>
                        )
                        ) : (
                            renderTextHeaderPreview()
                        )
                    }

                    <VariableDialog
                        open={variableDialogOpen}
                        anchorEl={variableDialogAnchorEl}
                        onClose={handleVariableDialogClose}
                        title="Variable"
                        variableData={createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data?.filter(
                            (field: any) =>
                                field.whatsapp_template_dynamic_variable_key?.toLowerCase() !== "{{headerimage}}" &&
                                field.whatsapp_template_dynamic_variable_key?.toLowerCase() !== "{{headervideo}}" &&
                                field.whatsapp_template_dynamic_variable_key?.toLowerCase() !== "{{headergif}}"
                        ) ?? []}
                        variableType="header"
                        activeVariableKey={activeVariableKey}
                        onChange={handleChange ?? (() => {})}
                        onRemoveField={handleRemoveField ?? (() => {})}
                        hasMissingSampleText={hasMissingSampleText}
                        // disabled={!!createTemplatePayload?.whatsapp_template_id}
                        disabled={!canUpdateTemplate(templateStatusDetails?.whatsapp_template_metadata?.meta_whatsapp_template_status_name, createTemplatePayload, whatsappTemplateTypes, draftId)}
                    />
                </>
            );
        }

        case "image":
        case "video":
        case "document":
        case "gif": {
            const label = headerType.charAt(0).toUpperCase() + headerType.slice(1);
            const content = template?.header?.content;
            const googleStorageId = template?.header?.google_storage_id;
            const headerData = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0];

            const imageSrc = googleStorageId
                ? `${getVendorField(vendorData, "backend api", process.env.REACT_APP_BASE_URL)}/api/storage/file?file_id=${googleStorageId}&flag_info=false`
                : content?.startsWith("http") ? content : null;

            const hasMedia = !!(imageSrc || content);
            const mediaUrl = imageSrc || content;

            const headerDataValue = headerData?.whatsapp_template_header_data_value
                || headerData?.whatsapp_template_header_variable_data?.[0]?.whatsapp_template_dynamic_variable_sample_value
                || content;

            const getDocumentDisplayName = (): string => {
                if (isEditable && files?.length) {
                    const matchedFile = files?.find((file: any) => (file?.file_data?.google_storage_id ?? file?.google_storage_id) === googleStorageId);
                    if (matchedFile?.name) return cleanFilename(matchedFile.name);
                }
                const headerDataValueString = headerDataValue;
                if (!headerDataValueString || typeof headerDataValueString !== "string" || headerDataValueString.includes("?") || headerDataValueString.includes("file_id=")) return "Document";
                const filename = headerDataValueString.includes("/") ? (headerDataValueString.split("/").pop() ?? headerDataValueString) : headerDataValueString;
                
                return cleanFilename(filename) || "Document";
            };

            const renderMediaHeaderContent = (isEditable: boolean) => (
                <>
                    {headerType === "image" && hasMedia ? (
                            <Box className={`${styles.whatsappPreview__mediaHeader} ${styles["whatsappPreview__mediaHeader--previewImage"]}`}>
                                <div className={styles.whatsappPreview__mediaHeader__previewInner}>
                                    <img alt="Header" src={mediaUrl || defaultWATemplateImg} />
                                </div>
                            </Box>
                        ) : headerType === "image" ? (
                            <Box className={`${styles.whatsappPreview__mediaHeader} ${styles[`whatsappPreview__mediaHeader--${headerType}`]} ${styles.whatsappPreview__textInputError}`}>
                                <Typography variant="caption" onClick={isEditable ? () => setEditingMedia(true) : undefined} sx={isEditable ? { cursor: "pointer" } : undefined}>
                                    {label}
                                </Typography>
                            </Box>
                        ) : null
                    }

                    {headerType === "video" && hasMedia ? (
                            <Box className={`${styles.whatsappPreview__mediaHeader} ${styles["whatsappPreview__mediaHeader--previewVideo"]}`}>
                                <div className={styles.whatsappPreview__mediaHeader__previewInner}>
                                    <video key={mediaUrl} controls className="video-tag">
                                        <source src={mediaUrl || ""} type="video/mp4" />
                                    </video>
                                </div>
                            </Box>
                        ) : headerType === "video" ? (
                            <Box className={`${styles.whatsappPreview__mediaHeader} ${styles[`whatsappPreview__mediaHeader--${headerType}`]} ${styles.whatsappPreview__textInputError}`}>
                                <Typography variant="caption" onClick={isEditable ? () => setEditingMedia(true) : undefined} sx={isEditable ? { cursor: "pointer" } : undefined}>
                                    {label}
                                </Typography>
                            </Box>
                        ) : null
                    }

                    {headerType === "document" && hasMedia ? (
                            <Box
                                className={`${styles.whatsappPreview__mediaHeader} ${styles["whatsappPreview__mediaHeader--previewDocument"]}`}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); mediaUrl && window.open(mediaUrl, "_blank"); } }}
                            >
                                <IconsColor iconName="documentFile" fillColor="#000" />
                                <Typography className={styles.whatsappPreview__documentPreviewFileName}>
                                    {getDocumentDisplayName()}
                                </Typography>
                                <OpenInNewIcon
                                    className={styles.whatsappPreview__openDocumentIcon}
                                    onClick={(e) => { e.stopPropagation(); mediaUrl && window.open(mediaUrl, "_blank"); }}
                                />
                            </Box>
                        ) : headerType === "document" ? (
                            <Box className={`${styles.whatsappPreview__mediaHeader} ${styles[`whatsappPreview__mediaHeader--${headerType}`]} ${styles.whatsappPreview__textInputError}`}>
                                <Typography variant="caption" onClick={isEditable ? () => setEditingMedia(true) : undefined} sx={isEditable ? { cursor: "pointer" } : undefined}>
                                    {label}
                                </Typography>
                            </Box>
                        ) : null
                    }

                    {headerType === "gif" && hasMedia ? (
                            <Box className={`${styles.whatsappPreview__mediaHeader} ${styles["whatsappPreview__mediaHeader--previewGif"]}`}>
                                <div className={styles.whatsappPreview__mediaHeader__previewInner}>
                                    <img alt="Header" src={mediaUrl || defaultWATemplateImg} />
                                </div>
                            </Box>
                        ) : headerType === "gif" ? (
                            <Box className={`${styles.whatsappPreview__mediaHeader} ${styles[`whatsappPreview__mediaHeader--${headerType}`]} ${styles.whatsappPreview__textInputError}`}>
                                <Typography variant="caption" onClick={isEditable ? () => setEditingMedia(true) : undefined} sx={isEditable ? { cursor: "pointer" } : undefined}>
                                    {label}
                                </Typography>
                            </Box>
                        ) : null
                    }
                </>
            );

            return isEditable ? (
                wrapWithHoverUI(
                    <Box className={hasMedia ? "" : `${styles.whatsappPreview__mediaHeader} ${styles[`whatsappPreview__mediaHeader--${headerType}`]}`}>
                        {!hasMedia && (editingMedia || hover) && uploadFileHandler && removeFileHandler ? (
                            <Box className={styles.whatsappPreview__mediaHeader__upload}>
                                <FileUpload
                                    uploadedFileDetails={files}
                                    multipleFileAllow={true}
                                    fileSizeAllowed={getWhatsappMediaSizeLimit(createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_type_name || "")}
                                    fileTypeAllowed={getWhatsappMediaAllowedFileTypes(createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_type_name || "")?.allowedType}
                                    fileNumberAllowed={2}
                                    isDisabled={createTemplatePayload?.whatsapp_template_id ? true : false}
                                    uploadFileHandler={uploadFileHandler}
                                    removeFileHandler={removeFileHandler}
                                    isDeleteFileIconVisible={isFileUploaded}
                                    bgColor={whiteColor}
                                    flagWhatsapp={true}
                                    flagShowJustIcon
                                    flagShowJustIconBgColor={greyColor}
                                />
                            </Box>
                        ) : (
                            renderMediaHeaderContent(true)
                        )}
                    </Box>
                )
            ) : (
                renderMediaHeaderContent(false)
            );
        } 
        case "location": {
            const locationVariableData = editable
                ? (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data ?? [])
                : (template?.header?.variableData || []);

            const locationFieldsVariableData = LOCATION_HEADER_FIELDS.map((field) => {
                const existingVariable = locationVariableData.find(
                    (variable: any) => variable?.whatsapp_template_dynamic_variable_key?.toLowerCase() === field.key.toLowerCase()
                );
                return {
                    whatsapp_template_dynamic_variable_key: existingVariable?.whatsapp_template_dynamic_variable_key ?? field.key,
                    whatsapp_template_dynamic_variable_sample_value: existingVariable?.whatsapp_template_dynamic_variable_sample_value ?? "",
                };
            });

            const hasMissingRequiredLocationSample = LOCATION_HEADER_FIELDS
                .filter((field) => field.required)
                .some((field) => {
                    const sampleValue = locationFieldsVariableData.find(
                        (variable) => variable.whatsapp_template_dynamic_variable_key?.toLowerCase() === field.key.toLowerCase()
                    )?.whatsapp_template_dynamic_variable_sample_value;
                    return !sampleValue || sampleValue.trim() === "";
                });

            const locationHeaderWarningText = getLocationHeaderSampleWarning(locationFieldsVariableData);
            const hasLocationHeaderWarning = !!locationHeaderWarningText;

            const requiredLocationVariableKeys = LOCATION_HEADER_FIELDS
                .filter((field) => field.required)
                .map((field) => field.key);

            const getLocationFieldVariableData = (fieldKey: string) =>
                locationFieldsVariableData.filter(
                    (variable) => variable.whatsapp_template_dynamic_variable_key?.toLowerCase() === fieldKey.toLowerCase()
                );

            const hasLocationFieldSampleValue = (fieldKey: string) => {
                const sampleValue = getLocationFieldVariableData(fieldKey)[0]?.whatsapp_template_dynamic_variable_sample_value;
                return !!(sampleValue && sampleValue.trim() !== "");
            };

            const renderLocationField = (fieldKey: string, className?: string) => {
                const fieldVariableData = getLocationFieldVariableData(fieldKey);
                const variableKey = fieldVariableData[0]?.whatsapp_template_dynamic_variable_key ?? fieldKey;

                return (
                    <Box
                        key={fieldKey}
                        className={`${styles.whatsappPreview__locationHeaderField} ${className ?? ""}`}
                    >
                        {renderVariableInText(variableKey, fieldVariableData)}
                    </Box>
                );
            };

            const locationDialogVariableData = LOCATION_HEADER_FIELDS
                .map((field) => getLocationFieldVariableData(field.key)[0])
                .filter(Boolean);

                const showLocationCoordinates = editable;
                const showLocationName = hasLocationFieldSampleValue("{{name}}") || editable;
                const showLocationAddress = hasLocationFieldSampleValue("{{address}}") || editable;
            const showLocationFields = showLocationName || showLocationAddress || showLocationCoordinates;

            const handleLocationVariableAreaClick = (e: React.MouseEvent) => {
                if (!editable) return;

                e.preventDefault();
                e.stopPropagation();

                const target = e.target as HTMLElement;
                const variableElement = target.closest?.('[data-variable="true"]') || target.closest?.(`.${styles.whatsappPreview__variable}`);

                setVariableDialogAnchorEl((variableElement as HTMLElement) || (e.currentTarget as HTMLElement));
                setActiveVariableKey(variableElement ? (variableElement as HTMLElement).getAttribute("data-variable-key") : null);
                setVariableDialogOpen(true);
            };

            const renderLocationHeaderPreview = () => (
                <Box
                    className={`${styles.whatsappPreview__locationHeader} ${styles.whatsappPreview__locationHeaderRadius} ${editable && hasLocationHeaderWarning ? styles.whatsappPreview__textInputError : ""}`}
                    onClick={editable ? handleLocationVariableAreaClick : undefined}
                    sx={editable ? { cursor: "pointer" } : undefined}
                >
                    <Box className={styles.whatsappPreview__locationHeaderMap}>
                        <LocationOnIcon className={styles.whatsappPreview__locationHeaderMapIcon} />
                    </Box>
                    {showLocationFields && (
                        <Box className={styles.whatsappPreview__locationHeaderFields}>
                            {showLocationName &&
                                renderLocationField("{{name}}", styles["whatsappPreview__locationHeaderField--name"])}
                            {showLocationAddress &&
                                renderLocationField("{{address}}")}
                            {showLocationCoordinates && (
                                <Box className={styles.whatsappPreview__locationHeaderCoordinates}>
                                    {renderLocationField("{{latitude}}")}
                                    {renderLocationField("{{longitude}}")}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            );

            return (
                <>
                    {editable ? wrapWithHoverUI(renderLocationHeaderPreview()) : renderLocationHeaderPreview()}

                    {editable && (
                        <VariableDialog
                            open={variableDialogOpen}
                            anchorEl={variableDialogAnchorEl}
                            onClose={handleVariableDialogClose}
                            title="Variable"
                            variableData={locationDialogVariableData}
                            variableType="header"
                            activeVariableKey={activeVariableKey}
                            onChange={handleChange ?? (() => {})}
                            onRemoveField={handleRemoveField ?? (() => {})}
                            hasMissingSampleText={hasMissingRequiredLocationSample}
                            warningText={locationHeaderWarningText}
                            hideDeleteButton
                            requiredVariableKeys={requiredLocationVariableKeys}
                            disabled={!canUpdateTemplate(templateStatusDetails?.whatsapp_template_metadata?.meta_whatsapp_template_status_name, createTemplatePayload, whatsappTemplateTypes, draftId)}
                        />
                    )}
                </>
            );
        }

        default:
            return null;
    }
};

export default WhatsAppMessageHeaderPreview;
